import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Loader2, CheckCircle2, PlayCircle } from "lucide-react";
import { api } from "../lib/api";
import ReferralsCard from "./csuite/platinum/ReferralsCard";

// Onboarding video URL (60s explainer). Set REACT_APP_ONBOARDING_VIDEO_URL in
// frontend/.env to a YouTube/Vimeo embed URL or a direct MP4. When empty, the
// success card falls back to a text checklist so the flow never breaks.
const ONBOARDING_VIDEO_URL = (process.env.REACT_APP_ONBOARDING_VIDEO_URL || "").trim();
const isEmbeddable = /^(https?:)?\/\/(www\.)?(youtube\.com|youtu\.be|player\.vimeo\.com|vimeo\.com)\//i.test(ONBOARDING_VIDEO_URL);
const isMp4 = /\.(mp4|webm|ogg)(\?|$)/i.test(ONBOARDING_VIDEO_URL);

function toYouTubeEmbed(url) {
  try {
    const u = new URL(url);
    if (u.hostname.includes("youtu.be")) {
      const id = u.pathname.replace(/^\//, "");
      return `https://www.youtube.com/embed/${id}?rel=0&modestbranding=1`;
    }
    if (u.hostname.includes("youtube.com")) {
      if (u.pathname.startsWith("/embed/")) return url;
      const id = u.searchParams.get("v");
      if (id) return `https://www.youtube.com/embed/${id}?rel=0&modestbranding=1`;
    }
    if (u.hostname.includes("vimeo.com") && !u.hostname.startsWith("player.")) {
      const id = u.pathname.replace(/^\//, "").split("/")[0];
      return `https://player.vimeo.com/video/${id}`;
    }
    return url;
  } catch { return url; }
}

function OnboardingVideo() {
  if (!ONBOARDING_VIDEO_URL) {
    return (
      <div className="mt-6 mb-4 text-left border border-[var(--border)] rounded-sm p-4 bg-zinc-50" data-testid="onboarding-checklist">
        <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2 flex items-center gap-1.5">
          <PlayCircle size={12}/> Get started in 60 seconds
        </div>
        <ol className="text-sm text-zinc-700 space-y-1.5 list-decimal pl-5">
          <li>Create your first project from the Portfolio Command Center.</li>
          <li>Pick a delivery methodology (Agile / Waterfall / Hybrid) — the Tracker adapts.</li>
          <li>Populate one domain (e.g. RAID or Fit-to-Standard) — everything autosaves.</li>
          <li>Ask NAVBOT for a status summary, then export a Board Pack.</li>
        </ol>
      </div>
    );
  }
  return (
    <div className="mt-6 mb-4" data-testid="onboarding-video">
      <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2 flex items-center gap-1.5 justify-center">
        <PlayCircle size={12}/> Watch · 60-second tour
      </div>
      <div className="relative w-full overflow-hidden rounded-sm border border-[var(--border)] bg-black" style={{ paddingTop: "56.25%" }}>
        {isEmbeddable ? (
          <iframe
            src={toYouTubeEmbed(ONBOARDING_VIDEO_URL)}
            title="Capittance onboarding"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="absolute inset-0 w-full h-full"
            data-testid="onboarding-video-iframe"
          />
        ) : isMp4 ? (
          <video src={ONBOARDING_VIDEO_URL} controls playsInline className="absolute inset-0 w-full h-full" data-testid="onboarding-video-mp4" />
        ) : (
          <iframe
            src={ONBOARDING_VIDEO_URL}
            title="Capittance onboarding"
            allowFullScreen
            className="absolute inset-0 w-full h-full"
            data-testid="onboarding-video-iframe-generic"
          />
        )}
      </div>
    </div>
  );
}

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [status, setStatus] = useState("checking");
  const [attempts, setAttempts] = useState(0);
  const sid = params.get("session_id");
  // Post-payment redirect target: prefer explicit ?next=, else the value we
  // stashed in sessionStorage at /csuite/verify → /signup handoff.
  const nextRaw = params.get("next") || (() => { try { return sessionStorage.getItem("harmonizer_signup_next") || ""; } catch { return ""; } })();
  const nextTarget = (nextRaw.startsWith("/") && !nextRaw.startsWith("//") && nextRaw.length < 200) ? nextRaw : "";
  const isCsuiteDest = nextTarget.startsWith("/csuite");
  const primaryLabel = isCsuiteDest ? "Enter Executive Portal →" : "Enter platform →";
  const primaryTarget = isCsuiteDest ? nextTarget : (nextTarget || "/projects?tour=1");
  const enterPortal = () => {
    try {
      if (!isCsuiteDest) localStorage.removeItem("harmonizer_tour_seen");
      sessionStorage.removeItem("harmonizer_signup_next");
    } catch { /* noop */ }
    nav(primaryTarget);
  };

  useEffect(() => {
    if (!sid) return;
    let t;
    const poll = async () => {
      try {
        const r = await api.subscribeStatus(sid);
        if (r.payment_status === "paid") setStatus("paid");
        else if (attempts > 20) setStatus("timeout");
        else { setAttempts(a => a + 1); t = setTimeout(poll, 2000); }
      } catch {
        if (attempts > 20) setStatus("timeout");
        else { setAttempts(a => a + 1); t = setTimeout(poll, 2500); }
      }
    };
    poll();
    return () => t && clearTimeout(t);
    // eslint-disable-next-line
  }, [sid, attempts]);

  return (
    <div className="min-h-screen grid place-items-center grid-bg py-8 px-4">
      <div className="card p-8 max-w-xl w-full text-center">
        {status === "checking" && <><Loader2 className="animate-spin mx-auto mb-3" /><div className="font-display text-lg font-bold">Confirming your subscription…</div><div className="text-sm text-zinc-500 mt-1">Attempt {attempts + 1}</div></>}
        {status === "paid" && <>
          <CheckCircle2 className="text-green-600 mx-auto mb-3" size={40}/>
          <div className="font-display text-2xl font-bold">Welcome to Harmonizer</div>
          <div className="text-sm text-zinc-500 mt-1">Your subscription is active. Take 60 seconds to see how it fits together.</div>
          <OnboardingVideo />
          <button className="btn btn-primary" onClick={enterPortal} data-testid="success-continue">{primaryLabel}</button>
          <div className="mt-6 text-left">
            <ReferralsCard variant="success" />
          </div>
        </>}
        {status === "timeout" && <><div className="font-display text-lg font-bold text-amber-700">Still processing…</div><div className="text-sm text-zinc-500 mt-1 mb-4">Payment confirmation is delayed. You&rsquo;ll receive an email shortly. Check <a href="/projects" className="underline">the platform</a> or email <a href="mailto:Requests@Capittance.com?subject=Payment%20confirmation%20delay" className="underline">Requests@Capittance.com</a>.</div></>}
        <div className="mt-6 pt-4 border-t border-[var(--border)] font-mono text-[10px] uppercase tracking-widest text-zinc-500">
          Support · <a href="mailto:Requests@Capittance.com?subject=Payment%20support" className="underline" data-testid="payment-success-support">Requests@Capittance.com</a>
        </div>
      </div>
    </div>
  );
}

export function PaymentCancel() {
  const nav = useNavigate();
  return (
    <div className="min-h-screen grid place-items-center grid-bg">
      <div className="card p-8 max-w-md text-center">
        <div className="font-display text-2xl font-bold">Checkout canceled</div>
        <div className="text-sm text-zinc-500 mt-1 mb-4">No charge was made. Try again anytime, or email us if something went wrong.</div>
        <button className="btn btn-primary" onClick={() => nav("/signup")}>Back to plans</button>
        <div className="mt-6 pt-4 border-t border-[var(--border)] font-mono text-[10px] uppercase tracking-widest text-zinc-500">
          Support · <a href="mailto:Requests@Capittance.com?subject=Checkout%20canceled" className="underline" data-testid="payment-cancel-support">Requests@Capittance.com</a>
        </div>
      </div>
    </div>
  );
}
